import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useTable, useSortBy, useFilters, usePagination } from 'react-table';
import { collection, query, onSnapshot, deleteDoc,doc } from 'firebase/firestore';
import { db } from '../../firebase';
import logger from '../../utils/logger';
import { NewInvoiceModal } from './Utils/NewInvoiceModal';
import { toast } from 'react-toastify';
import { formatCurrency } from '../Help/helper';
import DuplicateManager from '../Utils/DuplicateManager';
import { Button, Card, Badge, Input, EmptyState, SkeletonTable } from '../UI';
import { MagnifyingGlassIcon, EyeIcon, TrashIcon } from '@heroicons/react/20/solid';


const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showNewInvoiceModal, setShowNewInvoiceModal] = useState(false);
  const [showDuplicateManager, setShowDuplicateManager] = useState(false);
  const navigate = useNavigate();


  const handleDeleteInvoice = async (invoiceId) => {
    if (window.confirm("Are you sure you want to delete this invoice?")) {
      try {
        await deleteDoc(doc(db, "invoices", invoiceId));
        toast.success("Invoice deleted successfully.");
      } catch (error) {
        logger.error("Error deleting invoice: ", error);
        toast.error("Failed to delete invoice.");
      }
    }
  };



  useEffect(() => {
    setIsLoading(true);
    const q = query(collection(db, 'newInvoices'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const invoicesData = [];
      querySnapshot.forEach((doc) => {
        invoicesData.push({ ...doc.data(), id: doc.id });
      });
      setInvoices(invoicesData);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: 'Invoice Number',
        accessor: 'invoiceNumber', 
      },

      {
        Header: 'Customer Name',
        accessor: 'customerName', 
      },

      {
        Header: 'Job Card',
        accessor: 'jobCardNo', 
      },

      {
        Header: 'Receivable',
        accessor: 'receivable', 
        Cell: ({ value }) => formatCurrency(value),
      },

      {
        Header: 'Date',
        accessor: 'invoiceDate',
        Cell: ({ value }) => new Date(value?.seconds * 1000).toLocaleDateString(),
      },
      {
        Header: 'Payment Status',
        accessor: 'invoiceStatus',
        Cell: ({ value }) => {
          let variant = 'warning';
          if (value === 'Fully Paid') variant = 'success';
          else if (value === 'Partially Paid') variant = 'warning';
          else if (value === 'Unpaid') variant = 'danger';
          return <Badge variant={variant}>{value || 'Pending'}</Badge>;
        },
      },

      {
        Header: 'Actions',
        Cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(`/finance/invoices/${row.original.id}`)}
              className="text-primary-600 hover:text-primary-900 transition-colors p-1"
              title="View invoice"
            >
              <EyeIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => handleDeleteInvoice(row.original.id)}
              className="text-red-600 hover:text-red-900 transition-colors p-1"
              title="Delete invoice"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        ),
      },
    ],
    [navigate]
  );

  const data = useMemo(() => {
    return invoices.filter((invoice) =>
      Object.values(invoice).some((item) =>
        String(item).toLowerCase().includes(searchInput.toLowerCase())
      )
    );
  }, [invoices, searchInput]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page, // instead of 'rows', we'll use page
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    pageCount,
    gotoPage,
    prepareRow,
  } = useTable({ columns, data }, useFilters, useSortBy, usePagination);

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <Card className="mb-6">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Invoices</h1>
          <p className="text-sm text-gray-600">Manage and track all customer invoices</p>
        </div>
      </Card>

      <Card>
        {/* Search and Actions Bar */}
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
            <div className="flex-1 w-full sm:w-auto">
              <Input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search invoices..."
                icon={MagnifyingGlassIcon}
              />
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                onClick={() => setShowNewInvoiceModal(true)}
                variant="primary"
                size="md"
              >
                Create Invoice
              </Button>
              <Button
                onClick={() => setShowDuplicateManager(true)}
                variant="danger"
                size="md"
              >
                Find Duplicates
              </Button>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <SkeletonTable rows={10} columns={7} />
          ) : data.length === 0 ? (
            <EmptyState
              title="No invoices found"
              message="There are no invoices to display. Create your first invoice to get started."
              actionLabel="Create Invoice"
              onAction={() => setShowNewInvoiceModal(true)}
            />
          ) : (
            <>
              <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center space-x-1">
                            <span>{column.render('Header')}</span>
                            <span className="text-primary-600">
                              {column.isSorted ? (column.isSortedDesc ? ' ↓' : ' ↑') : ''}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} className="hover:bg-gray-50 transition-colors">
                        {row.cells.map((cell) => {
                          return (
                            <td {...cell.getCellProps()} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {cell.render('Cell')}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <Button
                      onClick={() => gotoPage(0)}
                      disabled={!canPreviousPage}
                      variant="secondary"
                      size="sm"
                    >
                      First
                    </Button>
                    <Button
                      onClick={() => previousPage()}
                      disabled={!canPreviousPage}
                      variant="secondary"
                      size="sm"
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={() => nextPage()}
                      disabled={!canNextPage}
                      variant="secondary"
                      size="sm"
                    >
                      Next
                    </Button>
                    <Button
                      onClick={() => gotoPage(pageCount - 1)}
                      disabled={!canNextPage}
                      variant="secondary"
                      size="sm"
                    >
                      Last
                    </Button>
                  </div>
                  <span className="text-sm text-gray-700">
                    Page <span className="font-medium">{pageOptions.indexOf(page) + 1}</span> of{' '}
                    <span className="font-medium">{pageOptions.length}</span>
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </Card>

      {showNewInvoiceModal && <NewInvoiceModal closeModal={() => setShowNewInvoiceModal(false)} />}

      <DuplicateManager
        isOpen={showDuplicateManager}
        onClose={() => setShowDuplicateManager(false)}
        items={invoices}
        collectionName="newInvoices"
        duplicateCriteria={['invoiceNumber', 'customerName', 'jobCardNo']}
        itemLabel="Invoice"
      />
    </div>
  );
};

export default Invoices;
